import { type Filter, FilterId } from '../../../filters/types/filters';
import { getV2CompanyFilters } from '../../../filters/utils/getV2CompanyFilters';
import type { ReportParameterConfig } from '../../PageConfigProvider/page-reducer';
import type {
  ExtractMode,
  SnowflakeFieldType,
} from '../../PageConfigProvider/types/item';
import {
  isListFilter,
  isStaticListFilter,
  isTreeFilter,
} from '../filter-type-guards';

export type ExecuteSavedQueryFieldValue = string | number | string[] | number[];

const selectionIdToNumberOrString = ({
  id,
}: {
  id: string | number;
}): string | number => {
  const lastId = String(id).split('_').pop();
  if (!lastId) return id;
  const num = Number(lastId);
  return Number.isFinite(num) ? num : lastId;
};

function extractTreeSelectionIds(
  filter: Filter,
  isPrimary: boolean,
  selectionLevelIds?: string[]
): (string | number)[] {
  if (!isTreeFilter(filter)) return [];
  const allSelections = isPrimary
    ? (filter.primarySelections ?? [])
    : filter.selections;

  const selections =
    selectionLevelIds !== undefined && selectionLevelIds.length > 0
      ? allSelections.filter((s) => selectionLevelIds.includes(s.levelId))
      : allSelections;

  if (selections.length === 0) return [];

  if (filter.id === FilterId.COMPANY) {
    const { company } = getV2CompanyFilters(filter);
    return (company ?? []).filter((n) => Number.isFinite(n));
  }

  return selections.map(selectionIdToNumberOrString);
}

function extractTreeSelectionLevelNames(
  filter: Filter,
  isPrimary: boolean,
  selectionLevelIds?: string[]
): string[] {
  if (!isTreeFilter(filter)) return [];
  const allSelections = isPrimary
    ? (filter.primarySelections ?? [])
    : filter.selections;

  const selections =
    selectionLevelIds !== undefined && selectionLevelIds.length > 0
      ? allSelections.filter((s) => selectionLevelIds.includes(s.levelId))
      : allSelections;

  if (selections.length === 0) return [];
  return [...new Set(selections.map((s) => s.levelId))];
}

function extractListSelectionIds(filter: Filter): (string | number)[] {
  if (isListFilter(filter) || isStaticListFilter(filter)) {
    const selections = filter.selections ?? [];
    return selections.map(selectionIdToNumberOrString);
  }
  return [];
}

export function getReportParameterFieldValue(
  parameter: ReportParameterConfig,
  filter: Filter | undefined,
  fieldType: SnowflakeFieldType,
  selectionLevelIds?: string[],
  extractMode?: ExtractMode
): ExecuteSavedQueryFieldValue | undefined {
  if (!filter) return undefined;

  if (extractMode === 'levelName' && isTreeFilter(filter)) {
    const levelNames = extractTreeSelectionLevelNames(
      filter,
      parameter.isPrimary,
      selectionLevelIds
    );
    if (levelNames.length === 0) return undefined;
    if (fieldType === 'string[]') return levelNames;
    return levelNames[0];
  }

  if (isTreeFilter(filter)) {
    const ids = extractTreeSelectionIds(
      filter,
      parameter.isPrimary,
      selectionLevelIds
    );
    if (ids.length === 0) return undefined;
    if (fieldType === 'number[]') {
      const nums = ids.filter((id): id is number => typeof id === 'number');
      return nums.length > 0 ? nums : undefined;
    }
    if (fieldType === 'string[]') {
      return ids.map(String);
    }
    if (fieldType === 'number') {
      const first = ids[0];
      return typeof first === 'number' ? first : undefined;
    }
    return String(ids[0]);
  }

  if (isListFilter(filter) || isStaticListFilter(filter)) {
    const ids = extractListSelectionIds(filter);
    if (ids.length === 0) return undefined;
    if (fieldType === 'number[]') {
      const nums = ids.filter((id): id is number => typeof id === 'number');
      return nums.length > 0 ? nums : undefined;
    }
    if (fieldType === 'string[]') {
      return ids.map(String);
    }
    if (fieldType === 'number') {
      const first = ids[0];
      return typeof first === 'number' ? first : undefined;
    }
    return String(ids[0]);
  }

  return undefined;
}
